import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ShieldCheck } from 'lucide-react';
import Footer from '../components/Footer';

const TermsOfService = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <img
                src="/assets/gt687tca.png"
                alt="Forsyt"
                className="h-8 w-auto"
              />
            </div>
            <Link to="/" className="inline-flex items-center text-sm font-medium text-gray-700 hover:text-blue-600">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Home
            </Link>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-6">
          <h1 className="text-4xl font-bold text-gray-900 mb-2" data-testid="terms-headline">Terms of Service</h1>
          <p className="text-gray-500 mb-2">Last updated: February 2026 &middot; Version 2.0</p>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 border border-emerald-100 mb-8" data-testid="terms-iso-chip">
            <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
            <span className="text-[11px] font-bold text-emerald-700 uppercase tracking-widest">ISO 27001 Certified &middot; GDPR Compliant</span>
          </div>

          <div className="prose prose-lg max-w-none">
            <p className="text-gray-700 leading-relaxed mb-6">
              These Terms of Service (&ldquo;Terms&rdquo;) govern access to and use of the websites, apps, and services
              (collectively, the &ldquo;Service&rdquo;) operated by <strong>Credos Forsyt Private Limited</strong>, a
              company incorporated in India and operating under the brand name &ldquo;Forsyt&rdquo; (&ldquo;Forsyt&rdquo;,
              &ldquo;we&rdquo;, &ldquo;us&rdquo;). By accessing or using the Service, you agree to be bound by these
              Terms. If you are using the Service on behalf of a company, you represent that you are authorised to
              accept these Terms for that company.
            </p>
            <p className="text-gray-700 leading-relaxed mb-8 text-sm bg-gray-50 border border-gray-200 rounded-lg p-4">
              <strong>Legal entity:</strong> Credos Forsyt Private Limited &middot;{' '}
              <strong>Registered office:</strong> New Delhi, India &middot;{' '}
              <strong>Operating brand:</strong> Forsyt &middot;{' '}
              <strong>Contact:</strong> legal@forsyt.com
            </p>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Accounts and Eligibility</h2>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>You must be at least 18 and have the legal capacity to enter into these Terms.</li>
                <li>You are responsible for all activity under your account and for keeping your credentials secure.</li>
                <li>We may suspend or terminate accounts that violate these Terms or pose security/abuse risks.</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Access to the Service</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Forsyt grants you a limited, non‑exclusive, non‑transferable, revocable right to access and use the Service during your subscription term, solely for your internal business purposes and in accordance with these Terms and any applicable order forms.
              </p>
              <p className="text-gray-700 leading-relaxed">
                We may modify or discontinue features from time to time, but will not materially reduce core functionality of a paid plan during a committed term without notice.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Customer Data and GTM Stack Integrations</h2>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>&ldquo;Customer Data&rdquo; includes CRM records, emails, call data, and other information you or your organisation connect or upload to the Service.</li>
                <li>You retain all rights to Customer Data.</li>
                <li>You grant Forsyt a worldwide, limited licence to host, process, analyse, and transmit Customer Data to provide, maintain, secure, and improve the Service and as otherwise described in our Privacy Policy and Data Processing Agreement.</li>
                <li>You are responsible for obtaining all necessary rights and consents to connect tools (e.g., CRM, call recorders, collaboration tools) and to allow Forsyt to process Customer Data as configured by your organisation.</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Acceptable Use</h2>
              <p className="text-gray-700 leading-relaxed mb-4">You agree not to:</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Use the Service in violation of any applicable law or regulation.</li>
                <li>Attempt to reverse engineer, decompile, or extract source code from the Service (except where permitted by law).</li>
                <li>Use the Service to build a competing product or service.</li>
                <li>Upload or transmit malicious code or carry out security attacks.</li>
                <li>Interfere with or disrupt the integrity or performance of the Service.</li>
                <li>Misrepresent identity or affiliation when using the Service.</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                We may suspend or restrict access where we reasonably believe there is a violation of this section or a risk to security, integrity, or other customers.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Subscription, Fees, Auto‑Renewal and Taxes</h2>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Access to paid features is provided on a subscription basis as described in your order form or online plan selection.</li>
                <li>Fees are typically billed in advance on a recurring basis (e.g., monthly or annually) and are non‑refundable except as expressly stated in our Refund Policy or your specific agreement.</li>
                <li><strong>Auto‑renewal.</strong> Subscriptions renew automatically at the then‑current pricing for the same term length unless either party gives written notice of non‑renewal at least <strong>sixty (60) days</strong> before the end of the current term. We will send a renewal reminder (by email and/or in‑product) at least sixty (60) days before each renewal date.</li>
                <li>You are responsible for applicable taxes, duties, and government charges (including GST/VAT where applicable), excluding Forsyt&rsquo;s income taxes.</li>
                <li>Where explicitly agreed, Forsyt may offer a &ldquo;No ROI, No Payment&rdquo; or similar performance‑based commercial model; any such model and its metrics will be governed by a separate written agreement or order form.</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Term and Termination</h2>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>These Terms remain in effect while you access or use the Service.</li>
                <li>Either party may terminate for cause upon written notice if the other party materially breaches these Terms and does not cure within 30 days of notice.</li>
                <li>You may stop using the Service at any time; cancellation will take effect at the end of the current billing period unless otherwise agreed.</li>
                <li>Upon termination or expiry, your access will cease and Forsyt will handle Customer Data in accordance with our data‑retention commitments, the Data Processing Agreement, and the Privacy Policy.</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Intellectual Property</h2>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Forsyt and its licensors own all rights, title, and interest in and to the Service, including software, models, interfaces, and related intellectual property, excluding Customer Data and third‑party content.</li>
                <li>You may provide feedback; Forsyt may use it without restriction or obligation.</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Forsyt IP Indemnity</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Forsyt will defend you against any third‑party claim alleging that the Service, when used in
                accordance with these Terms and applicable documentation, infringes that third party&rsquo;s
                patent, copyright, trade secret, or registered trademark, and will pay damages finally awarded
                by a court of competent jurisdiction (or amounts agreed in a settlement we authorise), subject
                to the liability cap in Section 11.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                If the Service becomes, or in Forsyt&rsquo;s reasonable opinion is likely to become, the subject of
                an infringement claim, Forsyt may, at its option: (a) procure for you the right to continue
                using the Service; (b) modify the Service so it is no longer infringing while preserving
                materially equivalent functionality; or (c) terminate the affected subscription and refund any
                pre‑paid, unused fees for the remainder of the term.
              </p>
              <p className="text-gray-700 leading-relaxed text-sm">
                <strong>Carve‑outs.</strong> This indemnity does not apply to claims arising from: (i) Customer
                Data or content provided by you; (ii) modifications to the Service made by you or by a third
                party at your direction; (iii) combination of the Service with products, data, or services not
                supplied by Forsyt where the claim would have been avoided absent such combination; or (iv) use
                of the Service after Forsyt notified you to stop because of an alleged infringement claim.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Confidentiality</h2>
              <p className="text-gray-700 leading-relaxed">
                Each party may access the other party&rsquo;s confidential information. The receiving party will use such information only to perform obligations or exercise rights under these Terms and will protect it with reasonable care. This section does not limit any stronger confidentiality terms in a separate agreement, including the mutual NDA executed at engagement kickoff.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Disclaimers</h2>
              <p className="text-gray-700 leading-relaxed">
                The Service is provided on an &ldquo;AS IS&rdquo; and &ldquo;AS AVAILABLE&rdquo; basis. Forsyt disclaims all warranties, express or implied, including merchantability, fitness for a particular purpose, and non‑infringement. Forsyt does not warrant that the Service will be uninterrupted, error‑free, or that AI‑generated recommendations will guarantee outcomes. Customer remains responsible for the human review of any AI‑generated suggestion before it is acted upon in a downstream system.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Limitation of Liability</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                To the maximum extent permitted by law, and except for the carve‑outs set out below:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Neither party will be liable for any indirect, incidental, consequential, special, or punitive damages, or loss of profits or revenues, arising out of or related to these Terms or the Service.</li>
                <li>Forsyt&rsquo;s total aggregate liability arising out of or related to the Service will not exceed the fees paid by you for the Service in the twelve (12) months preceding the event giving rise to the claim (or, for free trials, INR 10,000).</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                <strong>Carve‑outs (no cap).</strong> The cap above does not apply to: (a) Forsyt&rsquo;s
                indemnification obligations under Section 8 (IP Indemnity); (b) either party&rsquo;s breach of
                confidentiality obligations under Section 9 or under the mutual NDA; (c) either party&rsquo;s
                breach of data‑protection obligations under the Data Processing Agreement; (d) liability for
                death, personal injury, fraud, or wilful misconduct; or (e) amounts due and payable under an
                order form. These limitations do not apply where prohibited by law.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Customer Indemnity</h2>
              <p className="text-gray-700 leading-relaxed">
                You agree to indemnify and hold Forsyt harmless from claims arising out of or related to your unlawful use of the Service, violation of these Terms, or infringement of third‑party rights through Customer Data, except to the extent caused by Forsyt&rsquo;s breach of these Terms.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">13. Data Protection &amp; Sub‑processors</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Forsyt processes personal data on your behalf in accordance with our Data
                Processing Agreement (DPA), which is incorporated into these Terms by reference.
                The DPA is shared on request to{' '}
                <a href="mailto:privacy@forsyt.com?subject=DPA%20template%20request" className="text-blue-600 hover:underline">privacy@forsyt.com</a>{' '}
                after a mutual NDA is in place, and reflects the requirements of GDPR
                Art. 28, the UK Data Protection Act 2018, India&rsquo;s Digital Personal Data Protection Act,
                2023 (DPDPA), and Standard Contractual Clauses (Commission Implementing Decision (EU) 2021/914)
                for international transfers where applicable.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                <strong>AI sub‑processor disclosure.</strong> Forsyt&rsquo;s AI Deal Chat uses Anthropic Claude
                (Sonnet) as its underlying LLM. Anthropic&rsquo;s commercial terms guarantee zero training on
                customer prompts or responses. Prompts and intermediate context are processed in‑memory only
                and are not persisted by Forsyt after a session ends. The full sub‑processor list, including
                hosting region and data accessed, is published at{' '}
                <Link to="/sub-processors" className="text-blue-600 hover:underline">/sub-processors</Link>.
                We give thirty (30) calendar days&rsquo; notice before any change to that list.
              </p>
              <p className="text-gray-700 leading-relaxed text-sm">
                Customer remains the data controller for personal data submitted to the Service. Forsyt acts
                as the data processor and follows documented Customer instructions.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">14. Governing Law and Dispute Resolution</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                These Terms are governed by the laws of the Republic of India, without regard to
                conflict‑of‑laws principles. Any dispute arising out of or in connection with these Terms or
                the Service will be subject to the <strong>exclusive jurisdiction of the courts of New Delhi,
                India</strong>, unless otherwise required by mandatory applicable law.
              </p>
              <p className="text-gray-700 leading-relaxed">
                Before initiating formal proceedings, the parties will first attempt in good faith to resolve
                any dispute through written notice and a thirty (30) day informal escalation between named
                executive sponsors of each party.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">15. Changes to These Terms</h2>
              <p className="text-gray-700 leading-relaxed">
                We may update these Terms from time to time. If changes are material, we will provide notice (for example, via email or in‑product) at least thirty (30) days before they take effect. Continued use of the Service after updated Terms become effective constitutes acceptance of the changes.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">16. Contact</h2>
              <p className="text-gray-700 leading-relaxed">
                For questions about these Terms, contact:<br />
                <strong>Credos Forsyt Private Limited</strong><br />
                New Delhi, India<br />
                <strong>Legal &amp; billing:</strong> legal@forsyt.com &middot; <strong>Privacy, DPO &amp; security:</strong> privacy@forsyt.com
              </p>
            </section>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default TermsOfService;
